import { Head, Link, router } from "@inertiajs/react";
import { Layout } from "@/components/Layout";

type Court = {
  id: string; startTime: string; duration: number; court: string | null;
  matchType: "singles" | "doubles" | null; convertible: boolean;
};

type Props = {
  request: {
    id: string; sport: string; club: string; date: string; startTime: string; duration: number;
    mode: "release" | "sniper"; status: string; excludeBubble: boolean;
    courtPreference: string; releasesAt: string; released: boolean;
    deadline: string; attempts: number; lastError: string | null;
    courts: Court[];
  };
  queue: { position: number; total: number; ahead: number };
  forecast: {
    enough: boolean; observations: number; sampleWeeks: number;
    distribution: { free: number; count: number }[];
    chance: number | null; ahead: number; alreadyYours: boolean;
  };
};

// Only a request that might still act on its own can be cancelled.
const ACTIVE_STATUSES = ["pending", "processing", "partial"];

export default function Show({ request, queue, forecast }: Props) {
  return (
    <Layout>
      <Head title={`${request.date} ${request.startTime}`} />

      <Link href="/" className="text-sm text-neutral-500 hover:text-neutral-800 dark:hover:text-neutral-200">
        ← Your courts
      </Link>

      <h1 className="mt-3 text-2xl font-semibold tracking-tight">
        {formatDate(request.date)} · {request.startTime}
      </h1>
      <p className="mt-1 text-neutral-500">
        {request.duration}min {request.sport} at {request.club} ·{" "}
        {request.mode === "sniper" ? "watching for cancellations" : "booking at release"}
      </p>

      <dl className="mt-8 space-y-5">
        <Row label="Queue">
          {queue.total === 1 ? (
            <span>Only you have asked for this slot.</span>
          ) : (
            <span>
              <strong>{ordinal(queue.position)}</strong> of {queue.total} ·{" "}
              {queue.ahead === 0
                ? "you asked first"
                : `${queue.ahead} ${queue.ahead === 1 ? "person" : "people"} asked before you`}
            </span>
          )}
        </Row>

        <Row label="Opens">
          {request.released ? (
            <span>Already open — trying now.</span>
          ) : (
            <span>{formatDateTime(request.releasesAt)}</span>
          )}
        </Row>

        <Row label="Chance">
          <Chance forecast={forecast} />
        </Row>

        <Row label="Attempts">
          {request.attempts === 0 ? "None yet." : `${request.attempts} so far.`}
          {request.lastError && (
            <span className="mt-1 block text-neutral-400">{request.lastError}</span>
          )}
        </Row>
      </dl>

      {request.courts.length > 0 && (
        <div className="mt-8">
          <h2 className="text-sm uppercase tracking-wide text-neutral-500">Your court{request.courts.length === 1 ? "" : "s"}</h2>
          <ul className="mt-2 space-y-2">
            {request.courts.map((court) => (
              <li
                key={court.id}
                className="flex items-center justify-between rounded-lg border border-neutral-200 px-4 py-2.5 dark:border-neutral-800"
              >
                <span className="text-sm">
                  {court.court ?? "Court"} · {court.startTime} · {court.duration}min
                  {court.matchType && <span className="ml-2 text-neutral-500">({court.matchType})</span>}
                </span>
                {court.convertible && <ConvertButton court={court} />}
              </li>
            ))}
          </ul>
        </div>
      )}

      {ACTIVE_STATUSES.includes(request.status) ? (
        <button
          onClick={() => router.delete(`/booking_requests/${request.id}`)}
          className="mt-10 rounded-lg border border-neutral-300 px-3 py-1.5 text-sm text-neutral-600 hover:bg-neutral-100 dark:border-neutral-700 dark:text-neutral-300 dark:hover:bg-neutral-800"
        >
          Cancel this request
        </button>
      ) : (
        <p className="mt-10 text-sm text-neutral-500">
          This request is {request.status} — nothing further will happen.
        </p>
      )}
    </Layout>
  );
}

/**
 * Shows its working rather than just a percentage: a number with no visible
 * basis is either distrusted or over-trusted, and both are worse than seeing
 * the handful of past weeks it came from.
 */
function Chance({ forecast }: { forecast: Props["forecast"] }) {
  if (!forecast.enough) {
    return (
      <span className="text-neutral-500">
        Not enough history yet — {forecast.observations}{" "}
        {forecast.observations === 1 ? "observation" : "observations"} of this slot so far.
      </span>
    );
  }

  const percent = Math.round((forecast.chance ?? 0) * 100);
  const busiest = Math.max(...forecast.distribution.map((d) => d.count));
  // One court for you, plus one for each member ahead. This used to read a
  // "needed" field that no longer exists, so the comparison was against
  // undefined and every bar came out grey while the caption promised green.
  const needed = (forecast.ahead ?? 0) + 1;

  return (
    <div>
      <div className="flex items-baseline gap-2">
        <span className="text-2xl font-semibold tabular-nums">{percent}%</span>
        <span className="text-neutral-500">
          of past weeks had {needed === 1 ? "a court free for you" : `the ${needed} courts you would need`}
        </span>
      </div>

      <table className="mt-3 text-sm text-neutral-500">
        <tbody>
          {forecast.distribution.map(({ free, count }) => (
            <tr key={free}>
              <td className="w-28 py-0.5 pr-2 text-right tabular-nums">
                {free} court{free === 1 ? "" : "s"} bookable
              </td>
              <td className="py-0.5">
                <span
                  className={`inline-block h-2 rounded-sm ${free >= needed ? "bg-brand-500" : "bg-neutral-300 dark:bg-neutral-700"}`}
                  style={{ width: `${Math.max(4, (count / busiest) * 120)}px` }}
                />
                <span className="ml-2 tabular-nums">{count}×</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <p className="mt-2 text-xs leading-relaxed text-neutral-400">
        Courts <strong>bookable the moment booking opened</strong>, same weekday and
        time, across {forecast.observations} weeks of the past {forecast.sampleWeeks}.
        Green means enough for your position — {needed === 1 ? "one court" : `${needed} courts`}.
        It does not account for other members booking in the seconds after release,
        so treat it as the starting field rather than a promise.
      </p>
    </div>
  );
}

// Switch a tennis court between singles and doubles. It cancels and rebooks the
// same court, so it warns and is meant for well before the game.
function ConvertButton({ court }: { court: Court }) {
  const to = court.matchType === "singles" ? "doubles" : "singles";
  return (
    <button
      type="button"
      onClick={() => {
        if (
          confirm(
            `Switch this court to ${to}? It's briefly released and rebooked on the same court — ` +
              "best done well before the game, not right at release.",
          )
        ) {
          router.post(`/bookings/${court.id}/convert`, {}, { preserveScroll: true });
        }
      }}
      className="rounded-lg border border-neutral-300 px-3 py-1 text-sm text-neutral-600 hover:bg-neutral-100 dark:border-neutral-700 dark:text-neutral-300 dark:hover:bg-neutral-800"
    >
      Switch to {to}
    </button>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[7rem_1fr] gap-4">
      <dt className="pt-0.5 text-sm uppercase tracking-wide text-neutral-500">{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function ordinal(n: number) {
  const suffix = ["th", "st", "nd", "rd"][(n % 100 - 20) % 10] ?? ["th", "st", "nd", "rd"][n % 100] ?? "th";
  return `${n}${suffix}`;
}

function formatDate(iso: string) {
  return new Date(`${iso}T00:00:00`).toLocaleDateString("en-GB", {
    weekday: "long", day: "numeric", month: "long",
  });
}

function formatDateTime(iso: string) {
  return new Date(iso).toLocaleString("en-GB", {
    weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
  });
}
